/**
 * DECODERS AND THE CROSS-FILE TEXTURE POOL — the two things a 36-character
 * cast cannot be loaded without.
 *
 * ── Why the decoders are singletons ────────────────────────────────────────
 *
 * `DRACOLoader` and `KTX2Loader` are not plain parsers: each one owns a pool of
 * up to four Web Workers plus a compiled WASM module, created lazily on first
 * decode and never torn down. Constructing one PER MODEL — the obvious thing to
 * do inside `loaderFor()` — gives 144 loaders, 144 worker pools and 144 WASM
 * instantiations for one cast. That is not a slow path, it is a browser tab that
 * dies. So the loaders live here, once, and every `GLTFLoader` in the client
 * borrows the same pair.
 *
 * ── Why the texture cache is here and not in `GLTFLoader` ──────────────────
 *
 * three's `GLTFLoader` de-duplicates images WITHIN one glTF (`textureCache`,
 * keyed on `uri:sampler`) and has no idea another file exists. The optimised
 * cast is built the other way round: 36 characters reference ONE pool of KTX2
 * files, so the crowd skin that 22 players share is 22 identical `fetch`es and
 * 22 identical `texImage2D` uploads unless somebody keys a cache on the URL and
 * hands out the same `Texture`. That somebody is this file.
 *
 * The key is the CANONICALISED ABSOLUTE URL, and canonicalisation is the whole
 * trick. `LoaderUtils.resolveURL()` concatenates without normalising, so two
 * characters that both reference `../_shared/abc_512.ktx2` produce
 *
 *     /characters/cast-opt/arena_diaz/../_shared/abc_512.ktx2
 *     /characters/cast-opt/rivals_kovac/../_shared/abc_512.ktx2
 *
 * — two different strings for one file, and a cache keyed on them shares
 * nothing while looking like it works. `new URL()` collapses the `..` segments;
 * see `canonicalTextureUrl`.
 *
 * ── One `Texture`, not one `Source` with clones ────────────────────────────
 *
 * The pool hands the SAME `Texture` object to every character. The alternative
 * — cache a master and hand out `master.clone()`, which shares `.source` — was
 * considered and rejected. three keys the GL texture on `Source` *plus* a
 * sampler cache key (`getTextureCacheKey`: wrap, filters, anisotropy,
 * colorSpace), so clones are one upload only while their sampler state agrees
 * and are TWO the moment it does not. Identity is one upload unconditionally.
 *
 * What clones would buy is isolation: `GLTFParser.assignTexture` writes
 * `texture.colorSpace = SRGBColorSpace` onto whatever it is handed, so one file
 * using a pooled image as a base-colour map would flip the colour space for
 * every other file using it as linear data. That cannot happen with THIS pool,
 * because the pipeline's filenames are content hashes with the colour space
 * baked into the suffix (`..._512s.ktx2` vs `..._512l.ktx2`) — an sRGB use and
 * a linear use of the same pixels are two different files and two different
 * keys. If that ever stops being true, `share: false` is the escape hatch and
 * the fix is clones, not identity.
 *
 * ── The consequence nobody may forget ──────────────────────────────────────
 *
 * A shared texture is owned by the POOL, not by the character that happened to
 * ask for it first. Disposing it when one LOD tier is evicted black-holes every
 * other character still drawing with it, and the bug shows up minutes later in
 * a different player's shirt. `isSharedTexture()` exists so `disposeCast()` can
 * skip them; `clearSharedTextureCache()` is the only thing allowed to free one.
 */
import { LoaderUtils, Texture, type WebGLRenderer } from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import type { GLTFLoaderPlugin, GLTFParser } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Served from `packages/client/public/decoders/`, copied out of
 * `three/examples/jsm/libs/` at the version pinned in `package.json`. The
 * `gltf/` subset is the one `GLTFLoader` expects — it is the same decoder built
 * without the mesh-sequence/point-cloud paths a glTF can never contain, and it
 * is 40% smaller. Both the JS wrapper and the WASM sit there; `DRACOLoader`
 * picks WASM when `WebAssembly` exists and falls back to the JS build when it
 * does not, which is why both files must ship.
 */
export const DRACO_DECODER_PATH = '/decoders/draco/gltf/';

/** `basis_transcoder.js` + `.wasm`, same provenance. */
export const KTX2_TRANSCODER_PATH = '/decoders/basis/';

let dracoLoader: DRACOLoader | null = null;
/** True when a decoder was installed from outside — see `dracoDecodeUsable`. */
let dracoInstalled = false;
let ktx2Loader: KTX2Loader | null = null;
let ktx2Detected = false;
let warnedNoRenderer = false;

/**
 * The shared Draco decoder.
 *
 * ⚠ In bare Node (vitest) three's `DRACOLoader` cannot decode: it builds its
 * worker from `URL.createObjectURL(new Blob(...))` and instantiates a DOM
 * `Worker`, and Node has no `Worker` global. `setDracoLoader()` is the seam a
 * headless harness uses to install an in-process decoder instead; see
 * `nodeDracoLoader.ts`. Nothing here throws at import time.
 */
export function getDracoLoader(): DRACOLoader {
  if (!dracoLoader) {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
  }
  return dracoLoader;
}

/**
 * Install a decoder built elsewhere — a headless Node decoder in tests, or a
 * loader someone already preloaded. Passing `null` restores the default.
 * The previous singleton is disposed, because it may own a worker pool.
 */
export function setDracoLoader(loader: DRACOLoader | null): void {
  if (dracoLoader && dracoLoader !== loader) dracoLoader.dispose();
  dracoLoader = loader;
  dracoInstalled = loader !== null;
}

/**
 * Whether three's worker-based Draco decoder can run in this environment.
 * False in bare Node — see `getDracoLoader`.
 */
export function dracoWorkersAvailable(): boolean {
  return typeof Worker !== 'undefined' && typeof URL?.createObjectURL === 'function';
}

/**
 * Can a Draco GLB actually be decoded right now? True in any browser, and in
 * Node once a headless decoder has been installed. False means a Draco model
 * will fail to parse — which is worth saying out loud BEFORE the parse, because
 * the error three raises at that point (`Worker is not defined`) names nothing
 * useful.
 */
export function dracoDecodeUsable(): boolean {
  return dracoInstalled || dracoWorkersAvailable();
}

/**
 * The shared KTX2 transcoder.
 *
 * `detectSupport(renderer)` is what tells the transcoder which GPU format to
 * produce (ASTC / BPTC / DXT / ETC / uncompressed RGBA fallback). Without it the
 * loader has no target format and every character renders BLACK — no exception,
 * no console error, just a squad of silhouettes. The renderer usually does not
 * exist yet when the first preload is queued, so the renderer can arrive later
 * via `setKTX2Renderer()`; what may never happen is the detection being skipped
 * quietly, hence the loud complaint on first use without one.
 */
export function getKTX2Loader(renderer?: WebGLRenderer): KTX2Loader {
  if (!ktx2Loader) {
    ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath(KTX2_TRANSCODER_PATH);
  }
  if (renderer) setKTX2Renderer(renderer);
  if (!ktx2Detected && !warnedNoRenderer) {
    warnedNoRenderer = true;
    console.error(
      '[cast] KTX2Loader is being used before detectSupport(renderer) — no GPU ' +
        'texture format has been chosen. Every KTX2-textured character will draw ' +
        'BLACK and nothing will throw. Call setKTX2Renderer(renderer) as soon as ' +
        'the WebGLRenderer exists.',
    );
  }
  return ktx2Loader;
}

/** Hand the transcoder a renderer, whenever one shows up. Idempotent. */
export function setKTX2Renderer(renderer: WebGLRenderer): void {
  if (!ktx2Loader) {
    ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath(KTX2_TRANSCODER_PATH);
  }
  ktx2Loader.detectSupport(renderer);
  ktx2Detected = true;
  warnedNoRenderer = false;
}

/** Has a renderer ever been supplied? The benchmark and the banner ask. */
export function ktx2SupportDetected(): boolean {
  return ktx2Detected;
}

/** Free both worker pools. Tests and teardown; safe to call twice. */
export function disposeDecoders(): void {
  dracoLoader?.dispose();
  dracoLoader = null;
  dracoInstalled = false;
  ktx2Loader?.dispose();
  ktx2Loader = null;
  ktx2Detected = false;
  warnedNoRenderer = false;
}

// ── the cross-file texture pool ─────────────────────────────────────────────

interface SharedEntry {
  readonly url: string;
  promise: Promise<Texture | null>;
  /** Set once the promise resolves, so the pool can dispose what it owns. */
  texture: Texture | null;
}

const pool = new Map<string, SharedEntry>();
/** Membership test for `disposeCast()`. Weak so a cleared pool can be collected. */
const poolMembers = new WeakSet<Texture>();
let hits = 0;
let misses = 0;

/**
 * A base only used to normalise relative paths in environments with no
 * `location` (vitest). Never fetched — the resolved string that three actually
 * requests is built separately, by three.
 */
const CANON_BASE = 'https://cast.invalid/';

/**
 * The cache key: one file, one string, whatever route through the directory
 * tree named it. See the header — this is the difference between a pool that
 * shares and a pool that only appears to.
 */
export function canonicalTextureUrl(uri: string, path: string): string {
  const joined = LoaderUtils.resolveURL(uri, path);
  const base =
    typeof location !== 'undefined' && typeof location.href === 'string'
      ? location.href
      : CANON_BASE;
  try {
    return new URL(joined, base).href;
  } catch {
    return joined;
  }
}

/**
 * One load per URL, ever.
 *
 * The PROMISE is stored, not the texture: twenty-two players racing on startup
 * all reach this within the same tick, and storing the result would mean
 * twenty-two in-flight fetches settling into one cache entry — one upload's
 * worth of state and twenty-two uploads' worth of cost. A failed load is
 * evicted so a later attempt can retry rather than inheriting a permanent null.
 */
export function acquireSharedTexture(
  url: string,
  load: () => Promise<Texture | null>,
): Promise<Texture | null> {
  const have = pool.get(url);
  if (have) {
    hits++;
    return have.promise;
  }
  misses++;
  const entry: SharedEntry = { url, texture: null, promise: null as never };
  entry.promise = (async () => {
    const texture = await load();
    if (!texture) {
      // Do not cache a failure: a 404 mid-deploy would otherwise keep every
      // later character textureless until the page is reloaded.
      pool.delete(url);
      return null;
    }
    entry.texture = texture;
    poolMembers.add(texture);
    return texture;
  })();
  pool.set(url, entry);
  // A rejection must evict too, and must never surface as an unhandled one.
  entry.promise.catch(() => pool.delete(url));
  return entry.promise;
}

/**
 * Is this texture owned by the pool? `disposeCast()` asks before freeing
 * anything, because a pooled texture outlives the tier that first requested it.
 */
export function isSharedTexture(texture: Texture | null | undefined): boolean {
  return !!texture && poolMembers.has(texture);
}

/**
 * Proof, not assertion. A benchmark loads a squad and reads this: `entries` is
 * how many distinct files the pool holds, `hits` is how many loads did not
 * happen. 36 characters over a 785-file pool should show hits ≫ entries.
 *
 * `sources` is the one that maps to VRAM. three keys its GL texture on
 * `Texture.source` (plus a sampler cache key), not on the `Texture` — so N
 * textures sharing one `Source` with matching sampler state are ONE upload, and
 * counting `Texture` objects would either over- or under-state the saving
 * depending on how the cache hands them out. `entries === sources` is the
 * invariant this pool maintains: one file, one Source, one upload.
 */
export function sharedTextureStats(): {
  entries: number;
  hits: number;
  misses: number;
  sources: number;
} {
  const sources = new Set<unknown>();
  for (const entry of pool.values()) if (entry.texture) sources.add(entry.texture.source);
  return { entries: pool.size, hits, misses, sources: sources.size };
}

/** The ONLY place a pooled texture may be freed. Tests, teardown, level change. */
export function clearSharedTextureCache(): void {
  for (const entry of pool.values()) {
    if (!entry.texture) continue;
    // Drop membership BEFORE disposing: a freed texture is nobody's shared
    // texture any more, and leaving it in the set would make `disposeCast()`
    // keep politely stepping around a dead object forever.
    poolMembers.delete(entry.texture);
    entry.texture.dispose();
  }
  pool.clear();
  hits = 0;
  misses = 0;
}

// ── the GLTFLoader plugin ───────────────────────────────────────────────────

/**
 * ⚠ THE PLUGIN DELIBERATELY CLAIMS THE NAME `KHR_texture_basisu`.
 *
 * `GLTFParser._invokeOne` walks `Object.values(this.plugins)` and takes the
 * FIRST plugin that returns non-null. three registers its own
 * `GLTFTextureBasisUExtension` in the `GLTFLoader` constructor, so it is always
 * ahead of anything `loader.register()` adds afterwards — a pool plugin under
 * any other name would simply never be asked about a KTX2 texture, and the
 * sharing would silently not happen. `GLTFLoader.parse()` assigns plugins by
 * name (`plugins[plugin.name] = plugin`), so registering under the same name
 * REPLACES the built-in handler while keeping its (early) position. That is the
 * only hook three offers, and it is why this file, not `castLoader.ts`, owns
 * the name.
 *
 * Because it replaces the built-in, this plugin is responsible for every
 * texture in the file, not just the KTX2 ones:
 *
 *   - `decode: false` → a stub `Texture`, the long-standing
 *     `FG_SKIP_TEXTURE_DECODE` behaviour that lets a GLB parse in bare Node.
 *   - external URI → the pool, transcoding `.ktx2` through the shared
 *     `KTX2Loader` and everything else through the parser's own image loader.
 *   - embedded image (`bufferView`) or `data:` URI → not shareable across
 *     files; handed straight back to three.
 */
export interface CastTexturePluginOptions {
  /** False in headless contexts: no `createImageBitmap`, no GPU, no point. */
  readonly decode: boolean;
  /** False disables the cross-file pool (a benchmark measuring the baseline). */
  readonly share?: boolean;
}

const BASISU = 'KHR_texture_basisu';

export function castTexturePlugin(
  parser: GLTFParser,
  opts: CastTexturePluginOptions,
): GLTFLoaderPlugin {
  return {
    name: BASISU,
    loadTexture(textureIndex: number): Promise<Texture> | null {
      if (!opts.decode) return Promise.resolve(new Texture());

      const json = parser.json as {
        textures?: { source?: number; extensions?: Record<string, { source?: number }> }[];
        images?: { uri?: string; bufferView?: number }[];
      };
      const textureDef = json.textures?.[textureIndex];
      if (!textureDef) return null;

      const basis = textureDef.extensions?.[BASISU];
      const sourceIndex = basis?.source ?? textureDef.source;
      const isBasis = basis !== undefined;
      if (sourceIndex === undefined) return null;

      const sourceDef = json.images?.[sourceIndex];
      const uri = sourceDef?.uri;

      // Embedded or inline: three's own path, no pooling. A `bufferView` image
      // lives inside THIS file's binary chunk, so by construction no other file
      // can be referencing the same bytes.
      if (!uri || uri.startsWith('data:')) {
        if (!isBasis) return null;
        return parser.loadTextureImage(textureIndex, sourceIndex, getKTX2Loader());
      }

      const ktx2 = isBasis || /\.ktx2($|\?)/i.test(uri);
      const url = canonicalTextureUrl(uri, parser.options.path);
      const load = (): Promise<Texture | null> =>
        parser.loadTextureImage(
          textureIndex,
          sourceIndex,
          ktx2 ? getKTX2Loader() : parser.textureLoader,
        );

      if (opts.share === false) return load() as Promise<Texture>;

      // `loadTextureImage` resolves to `null` on failure (three swallows the
      // error); the pool evicts that so a retry is possible, and the parser
      // treats a null map the same way it always has.
      return acquireSharedTexture(url, load) as Promise<Texture>;
    },
  };
}
